import { AlertCircle, Eye, FileText, Plug, Sparkles, Target } from 'lucide-react';
import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import type { CommandCenter, ContentPageRow } from '@/services/marketingIntegrations.service';
import { diagnosePages, formatNumber } from './derive';

interface Props {
  cc: CommandCenter;
}

const CATEGORY_LABEL: Record<string, string> = {
  blog: 'Blog',
  guide: 'Guides',
  pricing: 'Pricing',
  product: 'Product',
  landing: 'Landing',
  other: 'Other',
};

export default function SiteContentTab({ cc }: Props) {
  const result = cc.contentPages;

  if (!result?.ok) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Plug className="h-8 w-8 text-slate-400" />}
          title="GA4 not connected"
          description={result?.error ?? 'Connect Google Analytics 4 to surface page-level performance and broken-funnel pages.'}
          action={{ label: 'Connect GA4', onClick: () => { window.location.href = '/admin/marketing-integrations'; } }}
        />
      </div>
    );
  }

  const pages: ContentPageRow[] = result.rows ?? [];
  const diagnosed = diagnosePages(pages);

  // Aggregate by category
  const categoryAgg = new Map<string, { sessions: number; engagedSessions: number; avgDuration: number; conversions: number; pages: number }>();
  for (const p of pages) {
    const c = p.category;
    const existing = categoryAgg.get(c) ?? { sessions: 0, engagedSessions: 0, avgDuration: 0, conversions: 0, pages: 0 };
    existing.sessions += p.sessions;
    existing.engagedSessions += Math.round(p.sessions * (p.engagementRate / 100));
    existing.avgDuration += p.avgDuration * p.sessions;
    existing.conversions += p.conversions;
    existing.pages += 1;
    categoryAgg.set(c, existing);
  }
  const categories = Array.from(categoryAgg.entries())
    .map(([c, v]) => ({
      category: c,
      label: CATEGORY_LABEL[c] ?? c,
      pages: v.pages,
      sessions: v.sessions,
      engagementRate: v.sessions > 0 ? (v.engagedSessions / v.sessions) * 100 : 0,
      avgDuration: v.sessions > 0 ? v.avgDuration / v.sessions : 0,
      conversions: v.conversions,
    }))
    .sort((a, b) => b.sessions - a.sessions);

  const broken = diagnosed.filter((p) => p.diagnosis === 'broken-funnel');
  const hidden = diagnosed.filter((p) => p.diagnosis === 'hidden-gem');

  // Estimated leads from fixing broken funnels (assume 2.5% baseline)
  const estLeadsPerMonth = broken.reduce((s, p) => s + p.estimatedLeadsPerMonth, 0);

  return (
    <div className="space-y-6 p-6">
      <MetricCardGrid columns={4}>
        <StatCard
          label="Tracked pages"
          value={formatNumber(pages.length)}
          subtitle="Top 100 by sessions"
          tone="blue"
          icon={<FileText className="h-4 w-4" />}
        />
        <StatCard
          label="Total sessions"
          value={formatNumber(pages.reduce((s, p) => s + p.sessions, 0))}
          subtitle="GA4 · last 30 days"
          tone="amber"
          icon={<Eye className="h-4 w-4" />}
        />
        <StatCard
          label="Broken funnels"
          value={formatNumber(broken.length)}
          subtitle="500+ sessions, 0 conversions"
          tone={broken.length > 0 ? 'red' : 'green'}
          icon={<AlertCircle className="h-4 w-4" />}
        />
        <StatCard
          label="Est. leads from fixing"
          value={formatNumber(estLeadsPerMonth)}
          subtitle="At 2.5% baseline conv. rate"
          tone="emerald"
          icon={<Target className="h-4 w-4" />}
        />
      </MetricCardGrid>

      {/* Category breakdown */}
      <Card>
        <CardHeader><CardTitle>Content categories</CardTitle></CardHeader>
        <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5 text-left font-semibold">Category</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Pages</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Sessions</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Engagement</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Avg duration</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Conversions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {categories.map((c) => (
                  <tr key={c.category} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-medium text-slate-900">{c.label}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{c.pages}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(c.sessions)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{c.engagementRate.toFixed(1)}%</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{c.avgDuration.toFixed(0)}s</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(Math.round(c.conversions))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Broken conversion paths */}
      {broken.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              Broken conversion paths
            </CardTitle>
          </CardHeader>
          <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
            <PageTable rows={broken.slice(0, 20)} fixHint="Add CTA + capture form. Wire conversion event." />
          </CardBody>
        </Card>
      )}

      {/* Hidden gems */}
      {hidden.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" />
              Hidden gems — high engagement, no CTA
            </CardTitle>
          </CardHeader>
          <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
            <PageTable rows={hidden.slice(0, 20)} fixHint="Add lead magnet + retargeting pixel." />
          </CardBody>
        </Card>
      )}

      {/* Fix playbook */}
      <Card>
        <CardHeader><CardTitle>Fix playbook</CardTitle></CardHeader>
        <CardBody>
          <ol className="space-y-3 text-sm text-slate-700">
            <li><strong className="text-slate-900">1. Wire a primary CTA</strong> on every broken-funnel page. Match it to the page intent (pricing → demo, blog → email capture, product → start-free).</li>
            <li><strong className="text-slate-900">2. Tag a conversion event</strong> for the new CTA in GA4. Until it's tagged, you can't tell whether the fix worked.</li>
            <li><strong className="text-slate-900">3. Drop a retargeting pixel</strong> on every "hidden gem" page so its engaged readers can be re-engaged via Meta + Google audiences.</li>
            <li><strong className="text-slate-900">4. Email capture on educational pages</strong> — exit-intent or scroll-triggered. The 80%+ engagement crowd is your best email-list source.</li>
            <li><strong className="text-slate-900">5. Internal linking</strong> from blog/guide content to product/pricing pages — this is the highest-ROI SEO change you can make.</li>
          </ol>
        </CardBody>
      </Card>
    </div>
  );
}

function PageTable({ rows, fixHint }: { rows: ReturnType<typeof diagnosePages>; fixHint: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5 text-left font-semibold">Path</th>
            <th className="px-3 py-2.5 text-left font-semibold">Category</th>
            <th className="px-3 py-2.5 text-right font-semibold">Sessions</th>
            <th className="px-3 py-2.5 text-right font-semibold">Engagement</th>
            <th className="px-3 py-2.5 text-right font-semibold">Conv.</th>
            <th className="px-3 py-2.5 text-right font-semibold">Est leads/mo</th>
            <th className="px-3 py-2.5 text-left font-semibold">Fix</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((p) => (
            <tr key={p.path} className="hover:bg-slate-50">
              <td className="px-4 py-2.5 max-w-[260px]">
                <a href={p.path} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono text-xs" title={p.path}>
                  {p.path.length > 38 ? p.path.slice(0, 35) + '…' : p.path}
                </a>
              </td>
              <td className="px-3 py-2.5"><Badge variant="info" size="sm">{CATEGORY_LABEL[p.category] ?? p.category}</Badge></td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(p.sessions)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{p.engagementRate.toFixed(1)}%</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(Math.round(p.conversions))}</td>
              <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{p.estimatedLeadsPerMonth}</td>
              <td className="px-3 py-2.5 text-xs text-slate-700">{fixHint}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
